import React, { FC, useEffect, useState } from 'react';

import { Avatar, Grid } from '@material-ui/core';
import RightIcon from '@material-ui/icons/KeyboardArrowRight';
import { Location, navigate } from '@reach/router';

import { IMetaSeasonAward, ISeasonAward } from '../../store/admin/seasonAwards/store';
import { IEventEnrollment } from '../../store/player/events/store';
import { IPlayer } from '../../store/user/store';
import { TStandingType } from '../../store/admin/eventAwards/store';

import { getPlayerProfileURI } from '../../contrib/utils';
import { sortBy } from '../../contrib/sortBy';
import { useBoolean } from '../../scenes/common/Hooks/useBoolean.hook';
import { formattedStandingTotal } from './Utils/formattedStandingTotal';
import { getStandingAmount } from './Utils/getStandingAmount';
import { getUserFullname } from './Utils/getUserFullname';
import { getUserImage } from './Utils/getUserImage';
import { isAward } from './Utils/isAward';
import { isEventEnrollment } from './Utils/isEventEnrollment';
import { isPlayer } from './Utils/isPlayer';
import { awardMotif } from './Utils/awardMotif';
import { awardDate } from './Utils/awardDate';
import { userPic } from '../../assets/images';
import { useRouteInfoState } from '../RouteInfo/Context/RouteInfo.context';
import { useGetPlayerEvent } from '../../contrib/Hooks/useGetPlayerEvent';
import { getEnrollmentsIdsInEventTeesheet } from '../Utils/getEnrollmentsIdsInEventTeesheet';

export type TItemType = IPlayer | IMetaSeasonAward | IEventEnrollment;

interface IProps {
  context?: any;
  item: TItemType;
}

const PlayerListItem: FC<IProps> = props => {
  const { item, context } = props;

  const { eventId } = useRouteInfoState();
  const event = useGetPlayerEvent(eventId);

  const standingType: TStandingType = props.context ? props.context.standingType : 'points';

  const clubId: number = props.context ? props.context.clubId : -1;

  const userImage = getUserImage(item);

  const userFullName = getUserFullname(item);

  const [isCollapsed, , onToggleCollapsed] = useBoolean();

  const { awards, total, position } = isAward(item) ? item : { awards: [], total: 0, position: 0 };

  const handleItemOnClick = (location: any) => () => {
    if (isPlayer(item)) {
      navigate(getPlayerProfileURI(location, clubId || -1, item.id));
    }

    if (isEventEnrollment(item)) {
      navigate(getPlayerProfileURI(location, clubId || -1, item.user.id));
    }

    if (isAward(item)) {
      if (context.openItem) {
        context.openItem(item.id);
      }

      setTimeout(
        () => {
          onToggleCollapsed();
        },
        isCollapsed ? 1000 * 0.2 : 0
      );
    }
  };

  const [itemStatusTag, setItemStatusTag] = useState('');

  useEffect(() => {
    if (isEventEnrollment(item) && event) {
      const enrollmentsIdsInEventTeesheet = getEnrollmentsIdsInEventTeesheet(event);

      const newItemStatusTag =
        item.status === 'active' || enrollmentsIdsInEventTeesheet.includes(item.id)
          ? 'in'
          : item.status === 'invited' || item.status === 'waiting'
          ? 'tbd'
          : 'out';

      setItemStatusTag(newItemStatusTag);
    }
  }, [item, event]);

  return isPlayer(item) || isEventEnrollment(item) || total > 0 ? (
    <Location>
      {({ location }) => (
        <div className="player-list-item-container">
          <div className="player-list-item" onClick={handleItemOnClick(location)}>
            <Grid className="grid" container={true} alignItems="center">
              {isAward(item) && (
                <Grid className="grid-item position" item={true} xs={1}>
                  {position}
                </Grid>
              )}

              <Grid className="grid-item avatar" item={true} xs={2}>
                <Avatar
                  className="avatar-inner"
                  src={userImage || userPic}
                  alt={`${userFullName} avatar`}
                />
              </Grid>

              <Grid className="grid-item player-name" item={true} xs={isAward(item) ? 6 : 8}>
                {userFullName}
              </Grid>

              <Grid className="grid-item last" item={true} xs={isAward(item) ? 2 : 2}>
                {isPlayer(item) ? (
                  <RightIcon className="arrow" />
                ) : isEventEnrollment(item) ? (
                  <div className={`tag ${itemStatusTag}`}>
                    <p>{itemStatusTag.toUpperCase()}</p>
                  </div>
                ) : (
                  formattedStandingTotal(standingType, total)
                )}
              </Grid>
            </Grid>
          </div>

          {!isCollapsed && isAward(item) && (
            <div className="awards-container">
              {sortBy(awards, 'dec', 'created').map((award: ISeasonAward, index: number) => (
                <div
                  className={`award ${index === awards.length - 1 ? 'last' : ''}`}
                  key={`${award.created}-${award.userId}-${index}`}
                >
                  <Grid className="grid" container={true} spacing={8}>
                    <Grid className="grid-item" item={true} xs={1} />

                    <Grid className="grid-item" item={true} xs={8}>
                      <div className="date">{awardDate(award)}</div>

                      <div className="type">{award.type}</div>

                      <div className="name">{`Tournament of Champions ${awardMotif(award)}`}</div>

                      <div className="note">
                        <i>{award.note}</i>
                      </div>
                    </Grid>

                    <Grid className="grid-item" item={true} xs={2}>
                      <div className="amount">{getStandingAmount(award, standingType)}</div>
                    </Grid>
                  </Grid>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Location>
  ) : (
    <></>
  );
};

export default PlayerListItem;
